import React, { useRef } from 'react';
import { Lock } from 'lucide-react';
import LoginHero from './LoginHero';
import LoginPhoneIntro from './LoginPhoneIntro';
import { LoginError } from './LoginControls';
import { useBrowserChromeColor } from '../../hooks/useBrowserChromeColor';

/**
 * The login page's frame, shared by `/login` and `/auth/action`: navy hero on
 * the left (the phone intro above the card below 768px), and the v2 card on
 * canvas with the screen's heading, one inline error and the step's body.
 */
interface LoginShellProps {
  title: string;
  subtitle: React.ReactNode;
  error?: string;
  /** Under the card, above the phone footer ("New here? Talk to our team"). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const LoginShell: React.FC<LoginShellProps> = ({ title, subtitle, error, footer, children }) => {
  // Login is the one screen that is navy from edge to edge, so the phone's
  // status bar and address bar have to come with it — otherwise the page
  // arrives framed by two white bands the app did not draw.
  const pageRef = useRef<HTMLDivElement>(null);
  useBrowserChromeColor(pageRef);

  return (
    <div ref={pageRef} className="flex h-app overflow-hidden bg-chrome font-sans max-md:h-auto max-md:min-h-app max-md:flex-col max-md:justify-center max-md:overflow-visible">
      <LoginHero />
      <LoginPhoneIntro />

      <div className="flex flex-1 items-center justify-center overflow-y-auto bg-canvas p-[32px] max-md:flex-none max-md:overflow-visible max-md:bg-transparent max-md:px-s5 max-md:pb-[30px] max-md:pt-0">
        <div className="w-full max-w-[360px]">
          <div className="rounded-panel border border-line bg-surface p-[32px] shadow-[0_12px_40px_-16px_rgba(16,24,40,0.12)] max-md:p-s6 max-md:shadow-floating">
            <h2 className="mb-s1 text-page-title text-ink">{title}</h2>
            <p className="mb-s7 text-body-md text-ink-secondary [overflow-wrap:anywhere]">{subtitle}</p>

            {error && <LoginError message={error} />}

            {children}
          </div>

          {footer}

          {/* The desktop hero's own footer, which a phone would otherwise lose
              along with the panel it lives in — and, since the wave above the
              card no longer carries a lockup, the one place the product says
              its name on this screen. */}
          <p className="m-0 mt-s6 flex flex-wrap items-center justify-center gap-x-s4 gap-y-s1 text-meta text-chrome-text opacity-70 md:hidden">
            <span className="inline-flex items-center gap-s2">
              <Lock className="h-[12px] w-[12px]" />
              Your BOMs stay private
            </span>
            <span>&copy; 2026 Hello Robbie</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginShell;
